<template>
	<div class="p-grid">
		<div class="p-col-12 p-pt-2 p-pb-1">
      <div class="p-grid p-ai-center vertical-container">
          <div class="p-col-fixed" style="width:55px;">
              <a @click="$functions.historyBack()" style="cursor: pointer">
                <img class="" src="assets/irinox/icons/arrow_back.svg" :alt="$t('common.arrow_left')"/>
              </a>
          </div>
          <div class="p-col">
              <h3>{{appliance.name ?? $functions.fixModelName(appliance.modelNumber)}}</h3>
          </div>
      </div>
        
        <div v-if="mfCycleRunning" class="irinox-form cycle-controls" style="overflow-x: hidden; margin: 0 -2rem;">

          <div class="fade-left"/>
          <div class="fade-right"/>

          <vueper-slides ref="phases" class="no-shadow ex--center-mode" 
              arrows-outside :bullets="false" 
              transition-speed="250" :infinite="false"
              :slideRatio="1/1"
              :disableArrowsOnEdges="true"
              @slide="changeSlide('slide', $event)"
              @ready="carouselReady"
              >      

            <template #arrow-left>
              <img src="assets/irinox/icons/carousel_left.svg"  :alt="$t('common.previous')" />
            </template>

            <template #arrow-right>
              <img src="assets/irinox/icons/carousel_right.svg"   :alt="$t('common.next')"/>
            </template>

            <vueper-slide
                v-for="i in appliance.cycle.cycleTotalPhases"
                :key="i"
                :title="i.toString()"
                :style="'background-color: white'" 
                >
              <template #content>
                <div v-if="i===mfCyclePhaseNumber" :class="['phase-header']">{{(i-1).toString().padStart(2, '0')}}. {{$t('mf.phases.'+appliance.cycle.phaseRunning).toUpperCase()}}</div>
                <div v-else :class="['phase-header',{'phase-done':i<mfCyclePhaseNumber}]">{{(i-1).toString().padStart(2, '0')}}.  {{$t('library.cycle.phase')}}</div>

                <div v-if="i<mfCyclePhaseNumber" :class="['phase','phase-done']">
                  <div> </div>
                  <div class="setpoint p-jc-center">{{$t('library.cycle.previous_phase')}}</div>
                  <div> </div>
                </div>

                <div v-else-if="i==mfCyclePhaseNumber" :class="['phase','phase-running']">
                  <div> </div>
                  <div class="setpoint" v-if="mfAirSetpointEnabled">
                    <div :class="['p-grid', 'p-ai-center']">
                      <div class="p-col-fixed p-text-center">
                        <span class="icon-air_temp"/>
                      </div>
                      <div class="p-col p-text-right">
                        <span v-if="mfCycleRunning">{{$functions.temperature(mfAirSetpoint, isCelsius)}}{{unit}}</span> 
                        <span v-else>-{{unit}}</span>  
                      </div>
                    </div>
                  </div>
                  <Divider v-if="mfAirSetpointEnabled" />
                  <div class="setpoint" v-if="mfCoreSetpointEnabled">
                    <div :class="['p-grid', 'p-ai-center']">
                      <div class="p-col-fixed p-text-center">
                        <span class="icon-core_temp"/>
                      </div>
                      <div class="p-col p-text-right">
                        <span v-if="mfCycleRunning">{{$functions.temperature(mfCoreSetpoint,isCelsius)}}{{unit}}</span> 
                        <span v-else>-{{unit}}</span>  
                      </div>
                    </div>
                  </div>
                  <Divider v-if="mfCoreSetpointEnabled" />
                  <div class="setpoint" v-if="!mfContinuousCycleRunning">
                    <div :class="['p-grid', 'p-ai-center']">
                      <div class="p-col-fixed p-text-center">
                        <span class="icon-time"/>
                      </div>
                      <div class="p-col p-text-right">
                        <span>{{mfDuration}}</span> 
                      </div>
                    </div>
                  </div>
                  <Divider v-if="!mfContinuousCycleRunning"/>
                  <div class="setpoint">
                    <div :class="['p-grid', 'p-ai-center']">
                      <div class="p-col-fixed p-text-center">
                        <span class="icon-fan"/>
                      </div>
                      <div class="p-col p-text-right">
                        <span v-if="mfCycleRunning">{{mfFanLevel}}</span> 
                        <span v-else>-</span> 
                      </div>
                    </div>
                  </div>
                  <div> </div>
                </div>

                <div v-else :class="['phase','phase-todo']">
                  <div> </div>
                  <div class="setpoint p-jc-center">{{$t('library.cycle.next_phase')}}</div>
                  <div> </div>
                </div>

              </template>
            </vueper-slide>
          </vueper-slides>

          <div v-if="!readOnlyMode" :style="{visibility: currentSlide==mfCyclePhaseNumber?'visible':'hidden'}">
            <div class="p-grid nested-grid p-jc-center controls">
              <div v-if="mfAirSetpointEnabled" class="p-col-fixed">
                <Control v-model="setpoints.temperature" icon="icon-air_temp" :isTemperature="true" :min="limits.cellSetTempMin" :max="limits.cellSetTempMax" @update:modelValue="startProgress" />
              </div>
              <div v-if="mfCoreSetpointEnabled" class="p-col-fixed">
                <Control v-model="setpoints.core" icon="icon-core_temp" :isTemperature="true" :min="limits.coreProbeSetTempMin" :max="limits.coreProbeSetTempMax" @update:modelValue="startProgress" />
              </div>
              <div v-if="!mfContinuousCycleRunning" class="p-col-fixed">
                <Control v-model="setpoints.time" icon="icon-time" :isTime="true" :min="limits.phaseDurationSetMin" :max="limits.phaseDurationSetMax" @update:modelValue="startProgress"/>
              </div>
              <div class="p-col-fixed">
                <Control v-model="setpoints.fan" icon="icon-fan" :min="limits.evapFanSetSpeedMin" :max="limits.evapFanSetSpeedMax" @update:modelValue="startProgress"/>
              </div>
            </div>
          </div>

          <div v-show="!mfContinuousCycleRunning" class="cycle-progress-bar p-grid">
            <div v-for="i in appliance.cycle.cycleTotalPhases" :key="'progress-'+i" 
              :class="['p-col','phase-progress-bar',{'phase-progress-bar-done':i<mfCyclePhaseNumber}]">
                <span v-if="i==mfCyclePhaseNumber" class="phase-progress-bar-running" :style="'width: '+progressBar+'%'"></span>
            </div>

          </div>

        </div>

        <div v-else>

          {{$t('mf.no_cycle_running')}}

        </div>
		</div>
	</div>

  <IrinoxDialogDark :visible="dialog.error" 
    mode="error" 
    :message="$t('details.dialog_error')"
    :life="3000" 
    @update:visible="dialog.error = $event" />


</template>

<script>
import ApplianceService from '../../../service/ApplianceService';
import EventHubListenerService from '../../../service/EventHubListenerService';
import IrinoxDialogDark from '../../IrinoxDialogDark.vue';
import { VueperSlides, VueperSlide } from 'vueperslides';
import 'vueperslides/dist/vueperslides.css';
// https://antoniandre.github.io/vueper-slides/?ref=madewithvuejs.com
import { mapGetters } from 'vuex';
import Control from '../Control.vue';
import commands from "../../../../public/json/commands_mf.json"

export default {
    components: {
      IrinoxDialogDark,
      VueperSlides, 
      VueperSlide,
      Control
    },
    data() {
        return {
          serialNumber: this.$route.params.serialNumber,
          commands: commands,
          dialog: {
            blockingError: false
          },
          currentSlide: null,
          setpoints: {
            temperature: 0,
            core: 0,
            humidity: 0,
            time: 0,
            fan: 0,
          },
          progressBar: 100
          // waitingForSaving: null,
        };
    },
    computed: {
        ...mapGetters({
          personaltoken: 'userdata/personaltoken',
          unit: 'userdata/unit',
          role: 'userdata/role',
          deviceTwin: 'appliances/deviceTwin',
          isCelsius: 'userdata/isCelsius',
        }),
        readOnlyMode: function() {
          return this.role.includes('INTERNAL') || this.role==='SERVICE_SALES';
        },
        appliance: function() {
            return this.$store.getters['appliances/appliance'](this.serialNumber);
        },
        mfCycleRunning: function() {
          return this.appliance.online && this.deviceTwin && this.deviceTwin.status && this.deviceTwin.status.cycleRunning && this.deviceTwin.status.cycleRunning === 1;
        },
        mfContinuousCycleRunning: function() {
          return !this.appliance.cycle || !this.appliance.cycle.cycleTimeLeft || this.appliance.cycle.cycleTimeLeft<=0;
          // return this.mfCycleRunning && this.appliance.cycle.functionRunning && this.appliance.cycle.functionRunning==13;
        },
        mfAirTemp: function() {
          return this.appliance.status && this.appliance.status.TCell? this.appliance.status.TCell : null;
        },
        mfCoreTemp: function() {
          return this.appliance.status && this.appliance.status.operativeCoreTemperature ? this.appliance.status.operativeCoreTemperature : null;
        },
        // mfIsCore: function() {
        //   return this.appliance.cycle && this.appliance.cycle.coreProbeCheck && this.appliance.cycle.coreProbeCheck==3;
        // },
        mfAirSetpoint: function() {
          return this.deviceTwin && this.deviceTwin.status ? this.deviceTwin.status.cellSetTemp : null;
        },
        mfCoreSetpoint: function() {
          return this.deviceTwin && this.deviceTwin.status ? this.deviceTwin.status.coreProbeSetTemp : null;
        },
        mfAirSetpointEnabled: function() {
          return this.limits.cellSetTempMin!=null && this.limits.cellSetTempMax!=null;
        },
        mfCoreSetpointEnabled: function() {
          return this.limits.coreProbeSetTempMin!=null && this.limits.coreProbeSetTempMax!=null;
        },
        mfFanLevel: function() {
          return this.deviceTwin && this.deviceTwin.status ? this.deviceTwin.status.evapFanSetSpeed : null;
        },
        mfDuration: function() {
          if(!this.mfContinuousCycleRunning) {
            try {
              //phaseDurationSet is in seconds
              let time = this.mfCycleRunning && this.deviceTwin.status ? this.deviceTwin.status.phaseDurationSet / 60 : null;
              //time in minutes
              if(time) {
                var hours = Math.floor(time / 60);
                time = time - hours * 60;
                var minutes = Math.floor(time);
                return (hours<10?"0"+hours:hours)+"h:"+(minutes<10?"0"+minutes:minutes)+"m";
              }
            } catch(err) {
              return "N/A";
            }
          }
          return "--h:--m";
        },
        mfDurationSetpoint: function() {
          return this.mfCycleRunning && this.deviceTwin.status ? this.deviceTwin.status.phaseDurationSet / 60 : 0;
        },
        mfTimeLeft: function() {
          return this.mfCycleRunning && this.appliance.cycle && this.appliance.cycle.cycleTimeLeft ? this.appliance.cycle.cycleTimeLeft / 60 : 0;
        },
        // mfPercentage: function() {
        //   if(this.mfDurationSetpoint>0 && this.mfTimeLeft>0) {
        //     return (this.mfDurationSetpoint-(this.mfTimeLeft/60)) * 100 / this.mfDurationSetpoint;
        //   } else {
        //     return 0;
        //   }
        // },
        mfCyclePhaseNumber: function() {
          return this.appliance.cycle.cyclePhaseNumber + 1;
        },
        mfHumidity: function() {
          return this.mfCycleRunning && this.deviceTwin.status ? this.deviceTwin.status.setLevelHumidity : 0;
        },
        limits: function() {
          if(this.mfCycleRunning) {
            try {
              let f = this.commands.functions[String(this.appliance.cycle.functionRunning)];
              let p = f.phases[String(this.appliance.cycle.phaseRunning)];
              if(p) {
                let lim = this.$functions.clone(p);
                if(lim.cellSetTempMin) {
                  lim.cellSetTempMin*=10;
                }
                if(lim.cellSetTempMax) {
                  lim.cellSetTempMax*=10;
                }
                if(lim.coreProbeSetTempMin) {
                  lim.coreProbeSetTempMin*=10;
                }
                if(lim.coreProbeSetTempMax) {
                  lim.coreProbeSetTempMax*=10;
                }
                return lim;
              }
            } catch(err) {
              console.warn("Error parsing commands " + err);
            }
          }
          return {
              name: null,
              cellSetTempMin: null,
              cellSetTempMax: null,
              coreProbeSetTempMin: null,
              coreProbeSetTempMax: null,
              setLevelHumidityMin: null,
              setLevelHumidityMax: null,
              evapFanSetSpeedMin: null,
              evapFanSetSpeedMax: null,
              phaseDurationSetMin: null,
              phaseDurationSetMax: null
            };
        }
    },
    watch: {
    },
    created() {
      // setInterval(function() {
      //   // if(this.mfContinuousCycleRunning) {
      //     if(this.progressBar>=100) {
      //       this.progressBar = 0;
      //     }
      //     this.progressBar+=0.25;
      //   // } else {
      //   //   this.progressBar = this.mfPercentage;
      //   // }
      // }.bind(this),10)
      this.applianceService = new ApplianceService();
      this.eventHubListenerService = new EventHubListenerService();
    },
    mounted() {
      // if(!this.$config.mock) {
        this.reloadAppliance();
      // }
      this.reloadDeviceTwin();
    },
    methods: {
      reloadAppliance() {
        this.applianceService.getAppliance(this.personaltoken, this.serialNumber)
          .then((data) => {
            if(data.code=="200") {
              let appliance = data.body;
              this.$store.commit('appliances/applianceDetails', appliance);
              if(appliance.online) {
                this.eventHubListenerService.postRunLive(this.personaltoken, this.serialNumber)
                  .then((data) => {
                    if(data.code=="200") {
                      console.log("RUN LIVE success")
                    } else {
                      console.error("RUN LIVE ERROR "+JSON.stringify(data));
                    }
                  })
                  .catch(error => {
                    console.error("RUN LIVE ERROR "+error);
                  });
              }
            } else {
              console.error(JSON.stringify(data));
              this.dialog.blockingError = true;
              setTimeout(function() {
                this.$router.push({ name: 'dashboard' });
              }.bind(this),3000);
            }
          })
          .catch(error => {
            console.error("ERROR "+error);
            this.dialog.blockingError = true;
            setTimeout(function() {
              this.$router.push({ name: 'dashboard' });
            }.bind(this),3000);
          });
      },
      reloadDeviceTwin() {
        this.eventHubListenerService.getDeviceTwin(this.personaltoken, this.serialNumber, this.appliance.type)
          .then((data) => {
            if(data.code=="200") {
              let dt = data.body;
              dt.serialNumber = this.serialNumber;
              this.$store.commit('appliances/deviceTwin', dt);
            } else {
              console.error("ERROR "+JSON.stringify(data));
              this.dialog.blockingError = true;
            }
          })
          .catch(error => {
            console.error("ERROR "+error);
            this.dialog.blockingError = true;
          });
      },
      carouselReady() {
        this.$refs.phases.goToSlide(this.mfCyclePhaseNumber - 1);
      },
      changeSlide(eventName, params) {
        console.log(JSON.stringify(params));
        this.currentSlide = params.currentSlide.index + 1;
        this.setpoints = {
            temperature: this.mfAirSetpoint,
            core: this.mfCoreSetpoint,
            humidity: this.mfHumidity,
            time: this.mfDurationSetpoint,
            fan: this.mfFanLevel,
          }

        if(this.limits.cellSetTempMin != null && this.setpoints.temperature < this.limits.cellSetTempMin) {
          this.setpoints.temperature = this.limits.cellSetTempMin;
        }
        if(this.limits.cellSetTempMax != null && this.setpoints.temperature > this.limits.cellSetTempMax) {
          this.setpoints.temperature = this.limits.cellSetTempMax;
        }

        if(this.limits.coreProbeSetTempMin != null && this.setpoints.core < this.limits.coreProbeSetTempMin) {
          this.setpoints.core = this.limits.coreProbeSetTempMin;
        }
        if(this.limits.coreProbeSetTempMax != null && this.setpoints.core > this.limits.coreProbeSetTempMax) {
          this.setpoints.core = this.limits.coreProbeSetTempMax;
        }

        if(this.limits.setLevelHumidityMin != null && this.setpoints.humidity < this.limits.setLevelHumidityMin) {
          this.setpoints.humidity = this.limits.setLevelHumidityMin;
        }
        if(this.limits.setLevelHumidityMax != null && this.setpoints.humidity > this.limits.setLevelHumidityMax) {
          this.setpoints.humidity = this.limits.setLevelHumidityMax;
        }

        if(this.limits.phaseDurationSetMin != null && this.setpoints.time < this.limits.phaseDurationSetMin) {
          this.setpoints.time = this.limits.phaseDurationSetMin;
        }
        if(this.limits.phaseDurationSetMax != null && this.setpoints.time > this.limits.phaseDurationSetMax) {
          this.setpoints.time = this.limits.phaseDurationSetMax;
        }

        if(this.limits.evapFanSetSpeedMin != null && this.setpoints.fan < this.limits.evapFanSetSpeedMin) {
          this.setpoints.fan = this.limits.evapFanSetSpeedMin;
        }
        if(this.limits.evapFanSetSpeedMax != null && this.setpoints.fan > this.limits.evapFanSetSpeedMax) {
          this.setpoints.fan = this.limits.evapFanSetSpeedMax;
        }

      },
      startProgress() {
        this.endProgress();
        this.waitingForSaving = setTimeout(() => {
          console.log(JSON.stringify(this.setpoints));
          this.saveParameter();
        },2000);
      },
      endProgress() {
        clearTimeout(this.waitingForSaving);
        this.waitingForSaving = null;
      },
      saveParameter() {

        let data = {};

        if(this.limits.cellSetTempMin!=null && this.limits.cellSetTempMax!=null) {
          data.cellSetTemp = this.setpoints.temperature;
        }

        if(this.limits.coreProbeSetTempMin!=null && this.limits.coreProbeSetTempMax!=null) {
          data.coreProbeSetTemp = this.setpoints.core;
        }

        if(this.setpoints.fan!=this.mfFanLevel 
            && this.limits.evapFanSetSpeedMin!=null && this.limits.evapFanSetSpeedMax!=null) {
          data.evapFanSetSpeed = this.setpoints.fan;
        }

        if(this.setpoints.humidity!=this.mfHumidity 
            && this.limits.setLevelHumidityMin!=null && this.limits.setLevelHumidityMax!=null) {
          data.setLevelHumidity = this.setpoints.humidity;
        }

        if(this.setpoints.time!=this.mfDurationSetpoint 
            && this.limits.phaseDurationSetMin!=null && this.limits.phaseDurationSetMax!=null) {
          data.phaseDurationSet = this.setpoints.time * 60;
        }

        this.eventHubListenerService.postSetSetpoint(this.personaltoken, this.serialNumber, data)
          .then((response) => {
            if(response.code=="200") {
              console.log("Setpoints updated: "+JSON.stringify(data));
              let dt = this.$functions.clone(this.deviceTwin);
              if(data.cellSetTemp!=null) {
                dt.status.cellSetTemp = data.cellSetTemp;
              }
              if(data.coreProbeSetTemp!=null) {
                dt.status.coreProbeSetTemp = data.coreProbeSetTemp;
              }
              if(data.evapFanSetSpeed!=null) {
                dt.status.evapFanSetSpeed = data.evapFanSetSpeed;
              }
              if(data.setLevelHumidity!=null) {
                dt.status.setLevelHumidity = data.setLevelHumidity;
              }
              if(data.time!=null) {
                dt.status.phaseDurationSet = data.phaseDurationSet;
              }
              this.$store.commit('appliances/deviceTwin', dt);
            } else {
              console.error("EXCEPTION "+JSON.stringify(response));
              this.dialog.error = true;
            }
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.dialog.error = true;
          });
      }
    }
}
</script>

<style scoped>

.layout-content {
  overflow-x: hidden;
}

</style>

<style>

.cycle-progress-bar {
  margin: 1rem 2rem 0;
  border: 1px solid #002445;
  padding: 1px;
  height: 15px;
}
.phase-progress-bar {
  background: #FFFFFF;
  padding: 0;
  border-left: 1px solid #002445;
}
.phase-progress-bar-done {
  background: #002445;
  border-left: 1px solid #FFFFFF;
}
.phase-progress-bar-running {
  background: #002445;
  height: 100%;
  display: block;
  border-left: 1px solid #FFFFFF;
}

.phase .p-col-12 {
  padding: 0;
}

.ex--center-mode {
  width: 400px;
  max-width: 80%;
  margin: auto;
}

.vueperslides__parallax-wrapper {
  overflow: visible;
}

.vueperslides__track {
  overflow: visible;
}

.vueperslides__arrow {
  color: rgba(169, 169, 169, 0.5);
  stroke-width: 2;
}

.vueperslide {
  /* padding: 25px; */
  -webkit-transform: scale(.75);
  transform: scale(.75);
}
.vueperslide .phase .p-divider {
  margin: 0;
}
.vueperslide .phase .setpoint {
  height: 50px;
  align-items: center;
  display: flex;
  width: 100%;
  font-size: 38px;
  font-weight: 600;
}
.vueperslide .phase .setpoint .p-grid {
  margin: 0;
  width: 100%;
}
.vueperslide .phase .setpoint .p-grid .p-col,
.vueperslide .phase .setpoint .p-grid .p-col-fixed {
  padding: 0 1rem;
}
.vueperslide .phase .setpoint .p-grid .p-col-fixed {
  display: flex;
}
.vueperslide .phase .setpoint [class^=icon-], 
.vueperslide .phase .setpoint [class*=" icon-"] {
  font-size: 45px;
}
.vueperslide .phase {
  width: 100%;
  height: calc(100% - 70px);
  background: #FFFFFF;
  box-sizing: border-box;
  border-radius: 0px 0px 10px 10px;
  overflow: hidden;
  border: 1px solid #002445;
  border-top: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  /* padding: 0.5rem 1rem; */
  padding: 0rem 1rem;
}
.vueperslide .phase.phase-running {
  border: 5px solid #002445;
}
.vueperslide .phase.phase-done {
  border: 1px solid #B4B4B4;
}
.vueperslide .phase-header {
  height: 70px;
  border-radius: 10px 10px 0px 0px;
  background: #002445;
  border: 1px solid #002445;
  font-size: 34px;
  font-weight: 600;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
}
/* .vueperslide .phase-header.phase-done {
  background: #C2410C;
  border: 1px solid #C2410C;
} */

.vueperslide.vueperslide--active {
  -webkit-transform: scale(1); 
  transform: scale(1);
}

.vueperslides__track-inner .vueperslide.vueperslide--active ~ .vueperslide {
  position: relative;
  left: 50px;
}
.vueperslides__track-inner .vueperslide {
  position: relative;
  left: -50px;
}
.vueperslides__track-inner .vueperslide.vueperslide--active {
  position: relative;
  left: 0px;
}

.vueperslides--rtl .vueperslides__arrows--outside .vueperslides__arrow--next, .vueperslides__arrows--outside .vueperslides__arrow--prev {
  left: -4em;
}
.vueperslides--rtl .vueperslides__arrows--outside .vueperslides__arrow--prev, .vueperslides__arrows--outside .vueperslides__arrow--next {
  right: -4em;
}

.fade-left {
  position: absolute;
  left: 82;
  background: linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 100%);
  height: 400px;
  width: calc(50% - 320px);
  z-index: 10;
}
.fade-right {
  position: absolute;
  right: 0;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 100%);
  height: 400px;
  width: calc(50% - 320px);
  z-index: 10;
}

.vueperslides__arrow svg {
  stroke-width: 2px;
}
.vueperslides__arrow svg:hover {
  stroke-width: 2px;
}

.cycle-controls .controls > div {
  width: 330px;
  padding: .5rem;
}
.cycle-controls .control > div {
  text-align: center;
}

.cycle-controls .p-divider.p-divider-horizontal:before {
  border-top-width: 2px;
}

</style>